// ReportTable — the one report table: Profit, Inventory, Trading, Balance
// sheet (and the trades log's desktop table when it moves over).
//
// Item 70 / §5.22 "Minimisation": the HEAD / CELL / NAME class strings, the
// empty row and the group / total row shapes were copied between the four
// reports; Trading had its own third shape with off-palette colours and no
// phone layout. This is the one table — right-aligned tabular numbers, a
// header that stays put, a horizontal scroller so the page never scrolls
// sideways, and one empty sentence.
//
// THE RULE IT ENFORCES (§6 "Empty state", §6 "Column header for time"): a
// column header is a short word the operator uses ("On hand", "Avg cost",
// "Margin · lifetime", "When"); an empty table says "Nothing in this
// period." — five words, no mechanism — and never a lone row of dashes.
//
// A note on the sticky header: `overflow-x-auto` makes the wrapper the scroll
// container, so the header sticks against the WRAPPER's scroll, not the
// page's. Pass `maxHeight` (e.g. "70vh") to give the wrapper a vertical
// scroll of its own; without it the header simply sits at the top.
//
// Usage:
//   <ReportTable
//     columns={[
//       { key: 'line', label: 'Book', align: 'left', width: '26%' },
//       { key: 'held', label: 'On hand' },
//       { key: 'cost', label: 'Avg cost' },
//       { key: 'margin', label: 'Margin · lifetime' },
//     ]}
//     rows={[
//       { key: 'usdt-aed', cells: { line: 'USDT · AED', held: <Figure value={q} asset="USDT" />, cost: fmtRate(c), margin: <Figure value={m} asset="AED" tone="signed" /> } },
//       { key: 'total', kind: 'total', cells: { line: 'Total', margin: <Figure value={t} asset="AED" tone="signed" strong /> } },
//     ]}
//   />

import type { ReactNode } from 'react';
import { fmtAmount } from '../lib/format';

export interface ReportColumn {
  key: string;
  label: ReactNode;
  /** Numbers are right-aligned (the default); the name column says `left`. */
  align?: 'left' | 'right';
  /** Optional CSS width for the column, e.g. "38%". */
  width?: string;
  /** Cells never wrap (the default) — a figure is one line. The name column
   *  of a sheet with long line names says `false` and wraps. */
  nowrap?: boolean;
}

/** One figure in a report cell. Absent is an em dash, never a zero — a blank
 *  cell and a real zero mean different things to someone reconciling.
 *  `signed` colours both ways (a margin); `plain` colours only a negative
 *  (a balance), the one negative token; `none` colours nothing. Item 70: Profit, Inventory and the
 *  Balance sheet each carried a copy of this; this is the one. Weight follows
 *  the emphasis rule (styles.css): every figure `.amt`, a total `.amt-key`. */
export function Figure({ value, asset, tone = 'plain', strong = false }: {
  value: number | null | undefined; asset?: string; tone?: 'plain' | 'signed' | 'none'; strong?: boolean;
}) {
  if (value === null || value === undefined) return <span className="text-tg-hint">—</span>;
  // `none`: no colour at all — an estimate on an assumed cost is neither a clean gain nor a clean loss.
  const colour = tone === 'none' ? ''
    : tone === 'signed'
      ? (value > 0 ? 'text-pos' : value < 0 ? 'text-neg' : '')
      : (value < 0 ? 'text-neg' : '');
  return (
    <span className={`${strong ? 'amt-key text-body' : 'amt'} ${colour}`}>
      {fmtAmount(value, asset)}
    </span>
  );
}

export interface ReportRow {
  key: string;
  cells: Record<string, ReactNode>;
  /** `group` heads a section (its first cell spans the row); `total` closes
   *  one; `sub` is a child of the line above it — a part of that line — and
   *  draws no rule of its own, so it reads as belonging to it. */
  kind?: 'line' | 'group' | 'total' | 'sub';
}

const HEAD = 'px-4 py-[9px] text-label uppercase tracking-[0.06em] text-tg-hint font-medium whitespace-nowrap';
const CELL = 'px-4 py-[7px] text-body';

const ROW: Record<NonNullable<ReportRow['kind']>, string> = {
  line:  'border-t border-stroke-soft',
  sub:   'border-t border-transparent',
  group: 'border-t border-stroke bg-panel-2/40',
  total: 'border-t border-stroke bg-panel-2 font-semibold',
};

function align(c: ReportColumn): string {
  const wrap = c.nowrap === false ? '' : ' whitespace-nowrap';
  return (c.align === 'left' ? 'text-left' : 'text-right font-mono tabular') + wrap;
}

export function ReportTable({ columns, rows, empty = 'Nothing in this period.', maxHeight, className = '', head = true, frame = true }: {
  columns: ReportColumn[];
  rows: ReportRow[];
  /** Five words or fewer, no mechanism. */
  empty?: string;
  maxHeight?: string;
  className?: string;
  /** `false` drops the column line — for a table whose header stands
   *  outside it (a phone block under a LedgerHead names its one currency
   *  there, and a line of empty column labels above the rows is a blank
   *  strip). The rows, their borders and their weights are the same. */
  head?: boolean;
  /** `false` drops the rounded frame — for a table that is one block of a
   *  screen (Profit's currency blocks on the phone stand flat under their
   *  LedgerHead, as Trades and Money's do). */
  frame?: boolean;
}) {
  const box = `${frame ? 'rounded-xl border border-stroke bg-panel' : ''} ${className}`;
  if (rows.length === 0) {
    return <div className={`${box} px-4 py-6 text-center text-meta text-tg-hint`}>{empty}</div>;
  }
  return (
    <div className={`${box} overflow-x-auto`} style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}>
      {/* Headless, the first row's own top line would double the line the
          caller's header already draws right above it. */}
      <table className={`w-full border-collapse ${head ? '' : '[&>tbody>tr:first-child]:border-t-0'}`}>
        {head && (
          <thead className="sticky top-0 z-[1] bg-panel shadow-[0_1px_0_0_theme(colors.stroke.DEFAULT)]">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={`${HEAD} ${c.align === 'left' ? 'text-left' : 'text-right'}`} style={c.width ? { width: c.width } : undefined}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((r) => {
            const kind = r.kind ?? 'line';
            if (kind === 'group') {
              const first = columns[0];
              return (
                <tr key={r.key} className={ROW.group}>
                  <td colSpan={columns.length} className={`${CELL} text-left font-semibold whitespace-nowrap`}>
                    {first ? r.cells[first.key] : null}
                  </td>
                </tr>
              );
            }
            return (
              <tr key={r.key} className={ROW[kind]}>
                {columns.map((c) => {
                  const v = r.cells[c.key];
                  return (
                    <td key={c.key} className={`${CELL} ${align(c)}`}>
                      {v === undefined || v === null || v === '' ? <span className="text-tg-hint">—</span> : v}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
